// pages/BroadbandInfoDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  MapPin,
  Building2,
  User,
  MoreVertical,
  FileText,
  Paperclip,
  Activity,
  Save,
  X as CloseIcon,
  Loader,
  Wifi,
  Globe,
  Calendar,
  CheckCircle,
  XCircle,
  Key,
  Network,
} from "lucide-react";
import apiService from "../../services/api";
import EditBroadbandInfoModal from "../../components/EditBroadbandInfoModal";
import { useTheme } from "../../contexts/ThemeContext";

const BroadbandInfoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [broadbandInfo, setBroadbandInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Notes & Attachments (if you want to add them later)
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  useEffect(() => {
    fetchBroadbandInfo();
  }, [id]);

  const fetchBroadbandInfo = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBroadbandInfo(id);
      setBroadbandInfo(response.data);
    } catch (error) {
      console.error("Error fetching broadband info:", error);
      navigate("/broadband-information");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this broadband information?",
      )
    ) {
      try {
        await apiService.deleteBroadbandInfo(id);
        navigate("/broadband-information");
      } catch (error) {
        console.error("Error deleting broadband info:", error);
        alert("Failed to delete broadband information");
      }
    }
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue || "");
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);
      await apiService.updateBroadbandInfo(id, { [field]: editingValue });
      await fetchBroadbandInfo();
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options = null,
    readOnly = false,
  }) => {
    const isEditing = editingField === field;

    if (readOnly) {
      return (
        <div>
          <label
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
          >
            {label}
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {value || "—"}
          </p>
        </div>
      );
    }

    return (
      <div className="group">
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              {options ? (
                <select
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                >
                  <option value="">Select...</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none resize-none`}
                  rows={3}
                  autoFocus
                />
              ) : type === "date" ? (
                <input
                  type="date"
                  value={editingValue ? editingValue.split("T")[0] : ""}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              ) : type === "password" ? (
                <input
                  type="password"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              ) : (
                <input
                  type={type}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              )}
              <button
                onClick={() => saveFieldEdit(field)}
                disabled={savingField}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingField ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
              >
                <CloseIcon
                  size={16}
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                />
              </button>
            </div>
          ) : (
            <>
              <p
                className={`${isDarkMode ? "text-white" : "text-gray-900"} flex-1`}
              >
                {type === "date" && value
                  ? new Date(value).toLocaleDateString()
                  : type === "password"
                    ? "••••••••"
                    : value || "—"}
              </p>
              <button
                onClick={() => startEditing(field, value)}
                className={`opacity-0 group-hover:opacity-100 p-1 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded transition-all`}
              >
                <Edit
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <Loader
          className={isDarkMode ? "text-white" : "text-gray-900"}
          size={32}
        />
      </div>
    );
  }

  if (!broadbandInfo) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Broadband information not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
              >
                <Edit size={16} />
                <span className="hidden sm:inline">Edit</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
                >
                  <MoreVertical
                    size={20}
                    className={isDarkMode ? "text-white" : "text-gray-900"}
                  />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg shadow-xl z-50`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${isDarkMode ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-700"} flex items-center gap-2`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} my-1`}
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} flex items-center gap-2 text-red-400`}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Broadband Info Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 flex items-center justify-center text-white flex-shrink-0">
                  <Wifi size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h1
                    className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {broadbandInfo.biRef}
                  </h1>
                  {broadbandInfo.type && (
                    <div
                      className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} text-sm`}
                    >
                      {broadbandInfo.type}
                    </div>
                  )}
                </div>

                {broadbandInfo.broadbandSupplier && (
                  <span className="px-3 py-1 rounded-lg border text-sm font-medium bg-blue-500/10 text-blue-400 border-blue-500/20">
                    {broadbandInfo.broadbandSupplier}
                  </span>
                )}

                {broadbandInfo.broadbandInstalled && (
                  <span className="px-3 py-1 rounded-lg border text-sm font-medium bg-green-500/10 text-green-400 border-green-500/20 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Installed
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {broadbandInfo.account && (
                  <button
                    onClick={() =>
                      navigate(`/accounts/${broadbandInfo.account.id}`)
                    }
                    className={`flex items-center gap-2 ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"} transition-colors`}
                  >
                    <Building2 size={16} />
                    <span>{broadbandInfo.account.name}</span>
                  </button>
                )}
                {broadbandInfo.project && (
                  <button
                    onClick={() =>
                      navigate(`/projects/${broadbandInfo.project.id}`)
                    }
                    className={`flex items-center gap-2 ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"} transition-colors`}
                  >
                    <FileText size={16} />
                    <span>{broadbandInfo.project.projectName}</span>
                  </button>
                )}
                {broadbandInfo.expectedSpeeds && (
                  <div className="flex items-center gap-2">
                    <Activity size={16} />
                    <span>{broadbandInfo.expectedSpeeds}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border`}
            >
              <div
                className={`flex border-b overflow-x-auto ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[
                  { id: "details", label: "Details", icon: FileText },
                  { id: "activity", label: "Activity", icon: Activity },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                        activeTab === tab.id
                          ? isDarkMode
                            ? "text-blue-400 border-b-2 border-blue-400"
                            : "text-blue-600 border-b-2 border-blue-600"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Wifi size={18} />
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="BI Reference"
                          field="biRef"
                          value={broadbandInfo.biRef}
                          readOnly
                        />
                        <EditableField
                          label="Broadband Supplier"
                          field="broadbandSupplier"
                          value={broadbandInfo.broadbandSupplier}
                          options={[
                            { value: "Gamma", label: "Gamma" },
                            { value: "Geocom", label: "Geocom" },
                            { value: "Openreach", label: "Openreach" },
                            { value: "Glide", label: "Glide" },
                            { value: "ITS", label: "ITS" },
                            { value: "Eclipse", label: "Eclipse" },
                            { value: "Elevate", label: "Elevate" },
                            { value: "Toob", label: "Toob" },
                            { value: "Giacom", label: "Giacom" },
                            { value: "G.Network", label: "G.Network" },
                            { value: "Other", label: "Other" },
                          ]}
                        />
                        <EditableField
                          label="Order Type"
                          field="orderType"
                          value={broadbandInfo.orderType}
                          options={[
                            { value: "New Provide", label: "New Provide" },
                            { value: "Upgrade", label: "Upgrade" },
                            { value: "Cease", label: "Cease" },
                            { value: "Transfer", label: "Transfer" },
                            { value: "Migrate", label: "Migrate" },
                          ]}
                        />
                        <EditableField
                          label="Type"
                          field="type"
                          value={broadbandInfo.type}
                          options={[
                            { value: "ADSL", label: "ADSL" },
                            { value: "SOGEA", label: "SOGEA" },
                            { value: "FTTP", label: "FTTP" },
                            { value: "FIBRE LIGHT", label: "FIBRE LIGHT" },
                            { value: "FIBRE BRIGHT", label: "FIBRE BRIGHT" },
                            { value: "ETHERNET", label: "ETHERNET" },
                            { value: "CITY FIBRE", label: "CITY FIBRE" },
                            { value: "STARLINK", label: "STARLINK" },
                            { value: "Mobile 4G", label: "Mobile 4G" },
                            { value: "Mobile 5G", label: "Mobile 5G" },
                            { value: "Voice Router", label: "Voice Router" },
                            { value: "Data Router", label: "Data Router" },
                            {
                              value: "Combined Router",
                              label: "Combined Router",
                            },
                            {
                              value: "Customer Owned",
                              label: "Customer Owned",
                            },
                          ]}
                        />
                        <EditableField
                          label="Expected Speeds"
                          field="expectedSpeeds"
                          value={broadbandInfo.expectedSpeeds}
                        />
                        <EditableField
                          label="Line Number"
                          field="lineNumber"
                          value={broadbandInfo.lineNumber}
                        />
                      </div>
                    </div>

                    {/* Network Information */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Network size={18} />
                        Network Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="WAN"
                          field="wan"
                          value={broadbandInfo.wan}
                        />
                        <EditableField
                          label="Local IP"
                          field="localIp"
                          value={broadbandInfo.localIp}
                        />
                        <EditableField
                          label="DSL Username"
                          field="dslUsername"
                          value={broadbandInfo.dslUsername}
                        />
                        <EditableField
                          label="DSL Password"
                          field="dslPassword"
                          value={broadbandInfo.dslPassword}
                        />
                      </div>
                    </div>

                    {/* Dates & Status */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Calendar size={18} />
                        Dates & Status
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Requested Date"
                          field="requestedDate"
                          value={broadbandInfo.requestedDate}
                          type="date"
                        />
                        <EditableField
                          label="Accepted Date"
                          field="acceptedDate"
                          value={broadbandInfo.acceptedDate}
                          type="date"
                        />
                        <div>
                          <label
                            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
                          >
                            Installation Status
                          </label>
                          <div className="flex items-center gap-2">
                            {broadbandInfo.broadbandInstalled ? (
                              <span className="px-3 py-2 rounded-lg bg-green-500/20 text-green-400 flex items-center gap-2">
                                <CheckCircle size={16} />
                                Installed
                              </span>
                            ) : (
                              <span className="px-3 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 flex items-center gap-2">
                                <XCircle size={16} />
                                Not Installed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Notes
                      </h3>
                      <EditableField
                        label=""
                        field="notes"
                        value={broadbandInfo.notes}
                        type="textarea"
                      />
                    </div>

                    {/* System Information */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created By
                          </label>
                          <p
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} flex items-center gap-2`}
                          >
                            <User size={16} />
                            {broadbandInfo.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(broadbandInfo.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified By
                          </label>
                          <p
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} flex items-center gap-2`}
                          >
                            <User size={16} />
                            {broadbandInfo.lastModifiedBy?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(broadbandInfo.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === "activity" && (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Activity size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Activity tracking coming soon</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Quick Information
              </h3>
              <div className="space-y-3">
                {broadbandInfo.account && (
                  <button
                    onClick={() =>
                      navigate(`/accounts/${broadbandInfo.account.id}`)
                    }
                    className={`w-full p-3 ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"} rounded-lg transition-colors text-left`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Building2 size={14} />
                      Account
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {broadbandInfo.account.name}
                    </div>
                  </button>
                )}

                {broadbandInfo.project && (
                  <button
                    onClick={() =>
                      navigate(`/projects/${broadbandInfo.project.id}`)
                    }
                    className={`w-full p-3 ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"} rounded-lg transition-colors text-left`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <FileText size={14} />
                      Project
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {broadbandInfo.project.projectName}
                    </div>
                  </button>
                )}

                {broadbandInfo.broadbandSupplier && (
                  <div
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"} rounded-lg`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Wifi size={14} />
                      Supplier
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {broadbandInfo.broadbandSupplier}
                    </div>
                  </div>
                )}

                {broadbandInfo.expectedSpeeds && (
                  <div
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"} rounded-lg`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Activity size={14} />
                      Expected Speeds
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {broadbandInfo.expectedSpeeds}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Card */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Installation
                  </span>
                  {broadbandInfo.broadbandInstalled ? (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1">
                      <CheckCircle size={12} />
                      Installed
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs flex items-center gap-1">
                      <XCircle size={12} />
                      Pending
                    </span>
                  )}
                </div>

                {broadbandInfo.requestedDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Requested
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(
                        broadbandInfo.requestedDate,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {broadbandInfo.acceptedDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Accepted
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(
                        broadbandInfo.acceptedDate,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditBroadbandInfoModal
          broadbandInfo={broadbandInfo}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchBroadbandInfo();
          }}
        />
      )}
    </div>
  );
};

export default BroadbandInfoDetail;
